import { Stack, Typography } from '@mui/material'
import CameraControls from 'camera-controls'
import { type EnhancedDomain, attachComponent } from 'higgins-domain'
import { useCallback, useState } from 'react'
import { ACESFilmicToneMapping, Color, PerspectiveCamera, SRGBColorSpace, Sphere, Vector3, WebGLRenderer } from 'three'

import { Legacy, useConfigurator } from '@salsita-ai/platform-project-sdk/configurator'
import {
  customPriceFooterComponent,
  customSummaryPageComponent,
  finalizeButtonComponent,
  memoEval,
  propertyPageComponent,
} from '@salsita-ai/platform-project-sdk/domain'
import {
  AttributeSwitchSelector,
  MaterialSwatchSelector,
  useActiveNavigationState,
  useAuthorizedClient,
  useDomain,
  useFinalizeViolations,
  usePatientObservableValue,
  useRootEntityPrice,
} from '@salsita-ai/platform-project-sdk/react'
import { PropertyContainer } from '@salsita-ai/platform-project-sdk/ui'

import { useRenderObjectThumbnail } from '../hooks/useRenderObjectThumbnail.js'
import { useHigginsAttributes, useHigginsLanguage } from '../systems/attributes'
import { configuratorInterface } from '../systems/configurator-interface.js'
import { Summary } from '../ui/Summary'

const ConditionalPriceFooter = ({ scene }: { scene: EnhancedDomain.$Scene }) => {
  const activeNavigationState = useActiveNavigationState()
  const formattedPrice = useRootEntityPrice()

  // Only show price when on Summary page AND showPrices is enabled
  const shouldShowPrice = activeNavigationState === 'Summary' && scene.showPrices

  if (!shouldShowPrice || !formattedPrice) {
    return <div />
  }

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography component="label" variant="body2" color="text.secondary" id="total-price-label">
        Total price
      </Typography>
      <Typography component="strong" variant="h6" aria-labelledby="total-price-label">
        {formattedPrice}
      </Typography>
    </Stack>
  )
}

const FinalizeButton = ({ scene }: { scene: EnhancedDomain.$Scene }) => {
  const { disabled, violations } = useFinalizeViolations()
  const { imageRenderingSystem, canvasSystem } = useConfigurator()
  const { configurationPersistSystem } = configuratorInterface.useDomainApplication()
  const { configurationId } = useDomain()
  const authorizedClient = useAuthorizedClient()
  const { session, addToCartUrl, cartUrl } = useHigginsAttributes()
  const language = useHigginsLanguage()

  const ready = usePatientObservableValue(canvasSystem.ready$, null)
  const threeScene = ready?.scene
  const { render: renderThumbnail } = useRenderObjectThumbnail(threeScene)

  const [processingAction, setProcessingAction] = useState<'add-to-cart' | 'send-pdf' | null>(null)

  const warningTooltip = violations?.find(v => v.violation.severity === 'warning')?.violation.message?.toString() ?? ''

  const generateImage = useCallback(
    async (finalizationUuid: string, fit: { center: Vector3; radius: number }, debug = false) => {
      const sharedConfig = {
        getCamera: async () => {
          const camera = new PerspectiveCamera(35, 1, 1000, 500000)
          const controls = new CameraControls(camera)
          await controls.fitToSphere(new Sphere(fit.center, fit.radius), false)
          await controls.rotateTo(-0.6, 1.2, false)
          controls.update(1000000)
          return camera
        },
        getRenderer: async (canvas: HTMLCanvasElement) => {
          const renderer = new WebGLRenderer({ antialias: true, canvas, preserveDrawingBuffer: true })
          renderer.outputColorSpace = SRGBColorSpace
          renderer.toneMapping = ACESFilmicToneMapping
          renderer.toneMappingExposure = 1.0
          return renderer
        },
        fileFormat: 'JPEG' as const,
        backgroundColor: new Color(0xf0f0f0),
      }
      await imageRenderingSystem.generateImages(
        finalizationUuid,
        [{ name: 'thumbnail', width: 256, height: 256, ...sharedConfig }],
        debug
      )
      await imageRenderingSystem.generateImages(
        finalizationUuid,
        [{ name: 'main', width: 1024, height: 1024, ...sharedConfig }],
        debug
      )
    },
    [imageRenderingSystem]
  )

  const generateProductGroupThumbnails = useCallback(
    async (finalizationUuid: string) => {
      const productGroups = scene.$productGroups
      if (!productGroups.length) return

      // Generate thumbnails for each unique product group
      for (const productGroup of productGroups) {
        try {
          // Find the 3D object for this segment
          const segmentObject = canvasSystem.findEntity3d({
            entityIds: [productGroup.representativeEntity.id],
            entityRoots: true,
          })?.[0]

          if (segmentObject) {
            // Generate thumbnail and upload to S3
            await renderThumbnail(
              [segmentObject],
              {
                width: 256,
                height: 256,
                padding: 1.2,
              },
              {
                finalizationUuid,
                imageNames: [productGroup.productCode],
              }
            )
          }
        } catch (error) {
          console.error(`Failed to generate thumbnail for product group ${productGroup.productCode}:`, error)
        }
      }
    },
    [scene.$productGroups, canvasSystem, renderThumbnail]
  )

  const calculateFit = ({ minX, maxX, height }: { minX: number; maxX: number; height: number }) => {
    const sceneWidth = Math.max(0, maxX - minX)
    const sceneHeight = Math.max(0, height)

    const centerX = (minX + maxX) / 2
    const center = new Vector3(centerX, sceneHeight / 2, 0)
    const halfDiagonal = Math.hypot(sceneWidth / 2, sceneHeight / 2)
    const radius = halfDiagonal * 1.05

    return { center, radius }
  }

  const handleAddToCartClick = async () => {
    if (disabled || processingAction) return

    setProcessingAction('add-to-cart')
    try {
      const finalizationUuid = crypto.randomUUID()

      await configurationPersistSystem.save()
      await generateImage(finalizationUuid, calculateFit(scene.$segmentTotals))
      await generateProductGroupThumbnails(finalizationUuid)

      if (!addToCartUrl) {
        throw new Error('Add to cart URL is not set')
      }

      if (!session) {
        throw new Error('Session is not set')
      }

      await authorizedClient.finalize(
        configurationId,
        finalizationUuid,
        {
          session,
          addToCartUrl: new URL(addToCartUrl, window.location.origin).toString(),
        },
        language
      )

      await authorizedClient.waitForFinalizationToComplete(configurationId, finalizationUuid)
      if (cartUrl) {
        window.location.href = cartUrl
      }
    } finally {
      setProcessingAction(null)
    }
  }

  const handleSendPdfClick = async () => {
    if (disabled || processingAction) return

    const emailAddress = window.prompt('Enter your email address to receive the PDF')?.trim()
    if (!emailAddress) return

    const isValidEmail = /^[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(emailAddress)
    if (!isValidEmail) {
      window.alert('Please enter a valid email address.')
      return
    }

    setProcessingAction('send-pdf')
    try {
      const finalizationUuid = crypto.randomUUID()

      await configurationPersistSystem.save()
      await generateImage(finalizationUuid, calculateFit(scene.$segmentTotals))
      await generateProductGroupThumbnails(finalizationUuid)

      await authorizedClient.finalize(
        configurationId,
        finalizationUuid,
        {
          emailAddress,
        },
        language
      )

      await authorizedClient.waitForFinalizationToComplete(configurationId, finalizationUuid)
      window.alert('Your PDF will be sent to ' + emailAddress + ' shortly.')
    } finally {
      setProcessingAction(null)
    }
  }

  return (
    <Stack gap={1}>
      <Legacy.ResponsiveFinalizeButton
        disabled={disabled || !!processingAction}
        hints={warningTooltip}
        title={processingAction === 'add-to-cart' ? 'Processing...' : 'Add to cart'}
        onClick={handleAddToCartClick}
      />
      <Legacy.ResponsiveFinalizeButton
        disabled={disabled || !!processingAction}
        hints={warningTooltip}
        title={processingAction === 'send-pdf' ? 'Processing...' : 'Send PDF'}
        onClick={handleSendPdfClick}
      />
    </Stack>
  )
}

export const sceneComponents = [
  attachComponent
    .withComponent(propertyPageComponent, 'main')
    .withEntity('Scene')
    .attach(
      memoEval(
        (self, _, ctx) => [self.id, self.includeCountertop, self.hasDifferentWallCabinetColor, ctx.mode],
        (entityId, includeCountertop, hasDifferentWallCabinetColor, mode) => ({
          page:
            mode === 'owner' ? (
              <PropertyContainer.Group spacing="medium">
                <AttributeSwitchSelector
                  entityId={entityId}
                  instanceName="includeCountertop"
                  title="Include countertop"
                />
                {includeCountertop && (
                  <PropertyContainer.Item title="Countertop Color" description="Choose the color of your countertops">
                    <MaterialSwatchSelector entityId={entityId} instanceName="countertopColor" />
                  </PropertyContainer.Item>
                )}
                <PropertyContainer.Item title="Hardware Material" description="Choose the material for your hardware">
                  <MaterialSwatchSelector entityId={entityId} instanceName="hardwareMaterial" />
                </PropertyContainer.Item>
                <PropertyContainer.Item title="Cabinet Color" description="Choose the color of your cabinets">
                  <MaterialSwatchSelector entityId={entityId} instanceName="cabinetColor" />
                </PropertyContainer.Item>
                <AttributeSwitchSelector
                  entityId={entityId}
                  instanceName="hasDifferentWallCabinetColor"
                  title="Define different color for wall cabinets"
                />
                {hasDifferentWallCabinetColor && (
                  <PropertyContainer.Item
                    title="Wall Cabinet Color"
                    description="Choose the color of your wall cabinets"
                  >
                    <MaterialSwatchSelector entityId={entityId} instanceName="wallCabinetColor" />
                  </PropertyContainer.Item>
                )}
                <PropertyContainer.Item title="Floor Texture" description="Choose the floor texture">
                  <MaterialSwatchSelector entityId={entityId} instanceName="floorTexture" />
                </PropertyContainer.Item>
              </PropertyContainer.Group>
            ) : (
              <Summary />
            ),
        })
      )
    ),
  attachComponent
    .withComponent(customSummaryPageComponent, 'summaryPage')
    .withEntity('Scene')
    .attach(
      memoEval(
        () => [],
        () => ({
          summaryNavigationState: 'Summary',
          page: <Summary />,
        })
      )
    ),
  attachComponent
    .withComponent(finalizeButtonComponent, 'default')
    .withEntity('Scene')
    .attach(
      memoEval(
        self => [self],
        self => ({
          button: <FinalizeButton scene={self} />,
        })
      )
    ),
  attachComponent
    .withComponent(customPriceFooterComponent, 'default')
    .withEntity('Scene')
    .attach(
      memoEval(
        self => [self],
        self => ({
          node: <ConditionalPriceFooter scene={self} />,
        })
      )
    ),
]
